// Interactive controls. Everything pressable in the app is a Pressable, a Button, a Chip or a
// ListRow: one focus ring, one press feedback, one disabled look, a 48px (56px accessible) target.
import "./config";
import { Text, View, XStack, styled } from "tamagui";
import { buttonType, pressed } from "./aria";

/** A native <button> with the app's focus ring, press feedback and disabled look; others build on it. */
export const Pressable = styled(View, {
  name: "Pressable",
  render: "button",
  ...buttonType(),
  cursor: "pointer",
  userSelect: "none",
  alignItems: "center",
  justifyContent: "center",
  borderWidth: 0,
  padding: 0,
  backgroundColor: "transparent",
  transitionProperty: "transform, background-color, border-color, opacity",
  transitionDuration: "var(--durFast)",
  scale: { default: 1, press: 0.98 },
  outlineWidth: { default: 0, "focus-visible": 3 },
  outlineStyle: "solid",
  outlineColor: "accent",
  outlineOffset: 3,
  variants: {
    disabled: { true: { opacity: 0.4, pointerEvents: "none" } },
    full: { true: { width: "100%" } },
  } as const,
});

export const ButtonLabel = styled(Text, {
  name: "ButtonLabel",
  fontFamily: "body",
  fontSize: "4",
  fontWeight: "800",
  color: "text",
  textAlign: "center",
});

const ButtonFrame = styled(Pressable, {
  name: "Button",
  flexDirection: "row",
  gap: "2",
  minHeight: "var(--uiTarget)",
  paddingHorizontal: "5",
  borderRadius: "2",
  borderWidth: "var(--borderW)",
  borderColor: "transparent",
  variants: {
    tone: {
      primary: { backgroundColor: "accent" },
      secondary: {
        backgroundColor: { default: "surface", press: "surface2" },
        borderColor: "lineStrong",
      },
      ghost: { backgroundColor: { default: "transparent", press: "accentSoft" } },
      danger: { backgroundColor: "errorBg", borderColor: "error" },
    },
    selected: { true: { backgroundColor: "accentSoft", borderColor: "accentLine" } },
  } as const,
  defaultVariants: { tone: "primary" },
});

const LABEL_TONE = {
  primary: "accentInk",
  secondary: "text",
  ghost: "accent",
  danger: "error",
} as const;

type Tone = keyof typeof LABEL_TONE;

export interface ButtonProps extends Omit<
  React.ComponentProps<typeof ButtonFrame>,
  "tone" | "selected" | "label"
> {
  tone?: Tone;
  /** A toggle's state; leave it out for a plain button. */
  selected?: boolean;
  /** The label; plain children are labelled the same way, so either spelling works. */
  label?: string;
  icon?: React.ReactNode;
  /** `submit` makes it the form's default action; every other button is `button`. */
  type?: "button" | "submit" | "reset";
}

/** The one button: primary (gold in the dark theme, garnet in light), secondary, ghost or danger. */
export function Button({
  label,
  icon,
  tone = "primary",
  selected,
  type = "button",
  children,
  ...rest
}: ButtonProps): React.JSX.Element {
  const labelColour = selected === true ? "accent" : LABEL_TONE[tone];
  const content = label ?? children;
  return (
    <ButtonFrame
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...buttonType(type)}
      tone={tone}
      data-tone={tone}
      selected={selected === true}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...pressed(selected)}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...rest}
    >
      {icon}
      {content === undefined ? null : <ButtonLabel color={labelColour}>{content}</ButtonLabel>}
    </ButtonFrame>
  );
}

/** A round icon-only button; give it `aria-label`, since the icon alone says nothing. */
export const IconButton = styled(Pressable, {
  name: "IconButton",
  flexShrink: 0,
  width: "var(--uiTarget)",
  height: "var(--uiTarget)",
  borderRadius: "pill",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: { default: "surface", press: "surface2" },
  variants: {
    look: {
      outlined: {},
      ghost: {
        borderColor: "transparent",
        backgroundColor: { default: "transparent", press: "accentSoft" },
      },
      accent: { borderColor: "transparent", backgroundColor: "accent" },
    },
  } as const,
});

/** Text that acts and sits quietly: a destructive "remove" or a "see all" under a list. */
export const TextButton = styled(Pressable, {
  name: "TextButton",
  flexDirection: "row",
  gap: "2",
  minHeight: "var(--uiTarget)",
  paddingHorizontal: "3",
  alignSelf: "center",
  variants: {
    tone: {
      accent: {},
      danger: {},
    },
  } as const,
  defaultVariants: { tone: "accent" },
});

const TEXT_BUTTON_TONE = { accent: "accent", danger: "error" } as const;

export interface TextButtonLabelProps extends Omit<
  React.ComponentProps<typeof TextButton>,
  "tone" | "label"
> {
  label: string;
  tone?: keyof typeof TEXT_BUTTON_TONE;
  icon?: React.ReactNode;
}

/** A TextButton with its label, underlined in the accessible version like every action link. */
export function TextAction({
  label,
  tone = "accent",
  icon,
  ...rest
}: TextButtonLabelProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <TextButton tone={tone} {...rest}>
      {icon}
      <ButtonLabel
        fontSize="3"
        fontWeight="700"
        color={TEXT_BUTTON_TONE[tone]}
        textDecorationLine={{ default: "none", a11yLight: "underline", a11yDark: "underline" }}
      >
        {label}
      </ButtonLabel>
    </TextButton>
  );
}

const chipLook = {
  flexDirection: "row",
  alignItems: "center",
  gap: "1",
  // The accessible version asks every control for 44 px; a chip is a control when it is pressed.
  minHeight: { default: 36, a11yLight: 44, a11yDark: 44 },
  paddingHorizontal: "3",
  paddingVertical: "1",
  borderRadius: "pill",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: "surface",
} as const;

const chipVariants = {
  active: {
    true: {
      backgroundColor: { default: "accentSoft", a11yLight: "accent", a11yDark: "accent" },
      borderColor: "accentLine",
    },
  },
  solid: { true: { backgroundColor: "accent", borderColor: "transparent" } },
  muted: { true: { backgroundColor: "surface2", borderColor: "transparent", minHeight: 28 } },
} as const;

const ChipFrame = styled(Pressable, { name: "Chip", ...chipLook, variants: chipVariants });
const TagFrame = styled(View, { name: "Tag", render: "span", ...chipLook, variants: chipVariants });

const ChipCount = styled(Text, {
  name: "ChipCount",
  fontFamily: "body",
  fontSize: "1",
  fontWeight: "700",
  color: "accentInk",
  backgroundColor: "accent",
  borderRadius: "pill",
  minWidth: 18,
  paddingHorizontal: 5,
  textAlign: "center",
  overflow: "hidden",
});

const ChipLabel = styled(Text, {
  name: "ChipLabel",
  fontFamily: "body",
  fontSize: "2",
  fontWeight: "700",
  color: "textDim",
  variants: {
    active: {
      true: { color: { default: "accent", a11yLight: "accentInk", a11yDark: "accentInk" } },
    },
    solid: { true: { color: "accentInk" } },
    compact: { true: { fontSize: "1" } },
    truncate: {
      true: {
        flexShrink: 1,
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
  } as const,
});

interface ChipLook {
  label: string;
  icon?: React.ReactNode;
  solid?: boolean;
  muted?: boolean;
  /** A small number after the label (how many are selected, how many are left). */
  count?: number | undefined;
  /** Something after the label (a "remove" cross on a chosen word). */
  trailing?: React.ReactNode;
}

export interface ChipProps
  extends
    Omit<React.ComponentProps<typeof ChipFrame>, "active" | "solid" | "muted" | "label">,
    ChipLook {
  /** A toggle's state; leave it out for a plain button. */
  active?: boolean;
  /** Keeps the chip inside its row: a long label is cut with an ellipsis instead of overflowing. */
  truncate?: boolean;
}

/** A small toggle: `active` is its state and its aria-pressed. For a label that does nothing use Tag. */
export function Chip({
  label,
  icon,
  active,
  solid,
  muted,
  count,
  trailing,
  truncate,
  ...rest
}: ChipProps): React.JSX.Element {
  return (
    <ChipFrame
      active={active === true}
      solid={solid === true}
      muted={muted === true}
      maxWidth={truncate === true ? "100%" : undefined}
      minWidth={truncate === true ? 0 : undefined}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...pressed(active)}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...rest}
    >
      {icon}
      <ChipLabel active={active === true} solid={solid === true} truncate={truncate === true}>
        {label}
      </ChipLabel>
      {count === undefined ? null : <ChipCount>{count}</ChipCount>}
      {trailing}
    </ChipFrame>
  );
}

export interface TagProps
  extends
    Omit<React.ComponentProps<typeof TagFrame>, "active" | "solid" | "muted" | "label">,
    ChipLook {}

/** A chip that only says something (a grape, a country, a vintage): no press, no focus stop. */
export function Tag({
  label,
  icon,
  solid,
  muted,
  count,
  trailing,
  ...rest
}: TagProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <TagFrame solid={solid === true} muted={muted === true} {...rest}>
      {icon}
      <ChipLabel solid={solid === true}>{label}</ChipLabel>
      {count === undefined ? null : <ChipCount>{count}</ChipCount>}
      {trailing}
    </TagFrame>
  );
}

/** A wrapping row of Chips or Tags. For one line that scrolls sideways use ScrollRow. */
export const Chips = styled(XStack, {
  name: "Chips",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "2",
});

const PillFrame = styled(Pressable, {
  name: "Pill",
  flex: 1,
  flexDirection: "row",
  gap: "2",
  minHeight: 44,
  paddingHorizontal: "3",
  paddingVertical: "2",
  borderRadius: "2",
  borderWidth: "var(--borderW)",
  borderColor: "line",
  backgroundColor: "surface",
  variants: {
    active: {
      true: {
        backgroundColor: { default: "accentSoft", a11yLight: "accent", a11yDark: "accent" },
        borderColor: "accent",
      },
    },
    // Sized by its label and growing evenly into what is left, so a few of them share one row.
    compact: {
      true: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: "auto",
        minWidth: 0,
        justifyContent: "center",
        paddingHorizontal: "2",
      },
    },
  } as const,
});

export interface PillProps extends Omit<React.ComponentProps<typeof PillFrame>, "active"> {
  /** The text; children stand in when a choice carries more than text (a colour dot). */
  label?: string;
  active: boolean;
  testId?: string;
}

/** A choice in a row of exclusive choices (depth, colour, clarity); lay a few out in a `Row`. */
export function Pill({ label, active, testId, children, ...rest }: PillProps): React.JSX.Element {
  return (
    <PillFrame
      active={active}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...pressed(active)}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(testId === undefined ? {} : { "data-testid": testId })}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...rest}
    >
      <ChipLabel active={active} compact={rest.compact === true}>
        {label ?? children}
      </ChipLabel>
    </PillFrame>
  );
}

/** Inline text that acts: a link (give it `href` through `render="a"`) or a quiet button. */
export const TextLink = styled(Text, {
  name: "TextLink",
  render: "a",
  fontFamily: "body",
  fontSize: "4",
  fontWeight: "700",
  color: "accent",
  cursor: "pointer",
  textDecorationLine: { default: "none", hover: "underline" },
  outlineWidth: { default: 0, "focus-visible": 3 },
  outlineStyle: "solid",
  outlineColor: "accent",
  outlineOffset: 2,
});
